import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, AlertTriangle, PencilIcon, XCircle, XIcon } from "lucide-react"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"

import { requestGMVChecking } from "@/api"
import { ArrowLeftIcon, BottomSheetModal, MessageError, TikTokIcon } from "@/components/shared"
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Image,
  Input
} from "@/components/ui"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { SalePropItem } from "@/features/jobs/explore/components/campaign/sale-prop-item"
// --------------------
// Shared hooks
// --------------------
import {
  ApplyCampaignContext,
  useApplyCampaignContext
} from "@/modules/helpers/context/ApplyCampaignContext"
import { ACTION_NATIVE, EAffiliateCampaignType, EValidateGMV } from "@/modules/helpers/enum"
import { useCurrentUser, useLayoutStore } from "@/modules/helpers/hooks/shared"
import { useShowCampaign } from "@/modules/helpers/hooks/shared/use-show-campaign"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { useApplyCampaignForm } from "@/modules/helpers/hooks/use-apply-campaign-form"
import { useSkuSelection } from "@/modules/helpers/hooks/use-sku-selection"
import { schemaInfoWorking } from "@/modules/helpers/schemas/select-skus.schema"
import { sendConnectTikTok, sendOpenLink } from "@/modules/helpers/utils"
import {
  ApplyButtonStatus,
  extractSaleProps,
  filterAvailableSaleProps,
  formatAddress
} from "@/modules/helpers/utils/campaign-form"
import {
  sanitizeNumericInput,
  toggleContentType,
  validateAddressInfo,
  validateNumericInput
} from "@/modules/helpers/utils/campaign-shared"
import { safeJsonParse } from "@/modules/helpers/utils/safe-json"
import { openTikTokShowcaseAuth } from "@/modules/helpers/utils/tiktok"
import type { AffiliateCampaign, User } from "@/modules/types"
import type {
  AddressInfo,
  ApplyCampaignFormProps,
  ModalConnectSocialProps,
  ModalGMVCheckingProps,
  ModalSelectSkuProps
} from "@/modules/types/jobs/campaign-form"

interface UseApplyCampaignModalsProps {
  modals: {
    validateChannel: boolean
    gmvChecking: boolean
    updateAddress: boolean
  }
  showSuccessModal: boolean
  user?: Partial<User> | null
  addressEditInfo: AddressInfo
  addressId?: string
  isPending: boolean
  product?: any
  onCloseModal: (modal: "validateChannel" | "gmvChecking" | "selectSku" | "updateAddress") => void
  onCloseSuccessModal: () => void
  onConnectSocialCompleted: () => void
  onGMVCheckingCompleted: () => void
  onAddressModalClose: () => void
  onAddressUpdateCompleted: (info: AddressInfo) => void
  addressCount?: number
}

function useApplyCampaignModals(props: UseApplyCampaignModalsProps) {
  return useMemo(
    () => ({
      handleCloseValidateChannel: () => props.onCloseModal("validateChannel"),
      handleCloseGmvChecking: () => props.onCloseModal("gmvChecking"),
      connectSocialProps: {
        visible: props.modals.validateChannel,
        userProfile: props.user,
        onClose: () => props.onCloseModal("validateChannel"),
        tikTokOnly: true,
        title: "Yêu cầu kết nối kênh",
        description:
          "Việc này giúp Nhãn hàng xem chỉ số kênh của bạn và tăng khả năng được xét duyệt.",
        onCompleted: props.onConnectSocialCompleted
      } as ModalConnectSocialProps,
      gmvCheckingProps: {
        visible: props.modals.gmvChecking,
        onClose: () => props.onCloseModal("gmvChecking"),
        onNext: props.onGMVCheckingCompleted
      } as ModalGMVCheckingProps,
      updateAddressProps: {
        visible: props.modals.updateAddress,
        isPending: props.isPending,
        onClose: props.onAddressModalClose,
        defaultValues: props.addressEditInfo,
        addressId: props.addressId,
        onCompleted: props.onAddressUpdateCompleted,
        addressCount: props.addressCount
      },
      successModalProps: {
        open: props.showSuccessModal,
        onClose: props.onCloseSuccessModal,
        product: props.product
      }
    }),
    [props]
  )
}

// --------------------
// Standalone modals
// --------------------

const ERROR_MESSAGES: Record<string, string> = {
  [ACTION_NATIVE.CONNECT_TIKTOK_EXISTED]: "Kênh Tiktok đã tồn tại",
  [ACTION_NATIVE.CONNECT_TIKTOK_ERROR]: "Lỗi kết nối kênh Tiktok",
  [ACTION_NATIVE.CONNECT_TIKTOK_FOLLOWER_COUNT_MUST_BE_MORE_THAN_1000]:
    "Số lượng người theo dõi phải lớn hơn 1000"
}

const ConnectSocialModal = memo(function ConnectSocialModal({
  visible,
  onCompleted,
  onClose,
  title = "Kết nối kênh mạng xã hội",
  description = "Kết nối các ứng dụng xã hội của bạn ngay bây giờ.",
  isConnected
}: ModalConnectSocialProps) {
  const [errorMessage, setErrorMessage] = useState<string>("")

  useEffect(() => {
    if (!visible) return

    const handleMessage = (event: MessageEvent) => {
      const data = safeJsonParse<any>(event.data)
      if (!data) return

      try {
        switch (data.type) {
          case ACTION_NATIVE.CONNECT_TIKTOK_SUCCESS:
            onCompleted?.()
            break
          default:
            if (ERROR_MESSAGES[data.type]) {
              setErrorMessage(ERROR_MESSAGES[data.type])
            }
            break
        }
      } catch {
        // ignore
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [visible, onCompleted])

  const handleConnect = useCallback(() => {
    if (!isConnected) {
      sendConnectTikTok()
    }
    setErrorMessage("")
  }, [isConnected])

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent className="max-w-[90%] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <Button
          className={cn("flex-1 bg-gradient-primary transition-transform rounded-xl h-12!", {
            "bg-gray-300 text-black": isConnected
          })}
          onClick={handleConnect}
        >
          <span className="flex items-center gap-2">
            {isConnected ? "Đã kết nối" : "Kết nối kênh Tiktok"} <TikTokIcon />
          </span>
        </Button>
      </DialogContent>
    </Dialog>
  )
})

const GMVCheckingModal = memo(function GMVCheckingModal({
  visible,
  onClose,
  onNext
}: ModalGMVCheckingProps) {
  const [error, setError] = useState<string>("")
  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationKey: ["requestGMVChecking"],
    mutationFn: () => requestGMVChecking(),
    onSuccess() {
      onNext()
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
      const type = error?.response?.data?.error.type
      if (
        type?.includes(EValidateGMV.NEED_MORE_PERMISSION) ||
        type?.includes(EValidateGMV.GMV_DATA_NOT_FOUND)
      ) {
        setError(EValidateGMV.NEED_MORE_PERMISSION)
      }
    }
  })

  const handleCheck = useCallback(() => {
    if (error === EValidateGMV.NEED_MORE_PERMISSION) {
      onNext()
    } else {
      mutate()
    }
  }, [error, onNext, mutate])

  const handleOpenLink = useCallback(() => {
    sendOpenLink(`${import.meta.env.VITE_WEB_BRAND_URL}/blogs/-id-9?webview=1`)
  }, [])

  const isSkipMode = error === EValidateGMV.NEED_MORE_PERMISSION

  return (
    <BottomSheetModal isVisible={visible} setIsVisible={onClose} title="Chiến Dịch Ưu Tiên">
      <div className="pb-3 px-3">
        {isSkipMode ? (
          <Alert variant="destructive" className="my-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 min-w-5 mt-0.5" size={18} />
            <AlertDescription>
              Vui lòng chia sẻ GMV theo hướng dẫn để được Nhãn hàng xét duyệt. Nếu bạn đã chia sẻ,
              hãy nhấn Bỏ qua.
            </AlertDescription>
          </Alert>
        ) : (
          <p className="text-sm font-medium pb-2">
            Nhãn hàng yêu cầu KOC mở quyền share GMV để tham gia. Vui lòng kiểm tra và mở quyền
            ngay!
          </p>
        )}

        <Image
          src="https://d12vp8vhqe3auw.cloudfront.net/blog_user_id_379/Nh%C3%A1p%20%283%29.png"
          alt="GMV hướng dẫn"
          width={500}
          height={200}
          className="w-full h-48 object-contain rounded-md mb-4"
        />

        <div className="flex gap-2 w-full">
          <Button
            className="flex-1 bg-linear-to-r from-primary to-secondary transition-transform rounded-xl h-14"
            disabled={isPending}
            onClick={handleCheck}
          >
            {isPending ? "Đang kiểm tra..." : isSkipMode ? "Bỏ qua" : "Kiểm tra GMV"}
          </Button>

          <Button
            variant="outline"
            className="flex-1 border-primary text-primary rounded-full h-14"
            onClick={handleOpenLink}
          >
            Xem chi tiết
          </Button>
        </div>
      </div>
    </BottomSheetModal>
  )
})

const SelectSkuModal = memo(function SelectSkuModal({
  open,
  onSelect,
  onClose,
  products,
  addressInfo,
  isPending,
  onUpdateAddress
}: ModalSelectSkuProps) {
  const { saleProp, togglePropId, findSelectedSku } = useSkuSelection({
    products
  })

  const form = useForm({
    defaultValues: {
      sku: "",
      totalClip: "",
      contentType: [EAffiliateCampaignType.VIDEO] as EAffiliateCampaignType[],
      addressInfo: ""
    },
    resolver: yupResolver(schemaInfoWorking)
  })

  const saleProps = useMemo(() => {
    const rawSaleProps = Array.isArray(products?.saleProps)
      ? products?.saleProps
      : extractSaleProps(products?.skus || [])
    return filterAvailableSaleProps(rawSaleProps, products?.skus || [])
  }, [products?.saleProps, products?.skus])

  const onSubmit = useCallback(
    (data: { totalClip: string; contentType: EAffiliateCampaignType[] }) => {
      if (!data.contentType?.length) {
        form.setError("contentType", { message: "Chọn hình thức lên bài." })
        return
      }

      const sku = findSelectedSku(saleProp)
      if (!sku && products?.skus?.length) {
        form.setError("sku", { message: "Chọn sản phẩm mẫu." })
        return
      }

      if (!validateAddressInfo(addressInfo)) {
        form.setError("addressInfo", { message: "Hãy cập nhật thông tin nhận hàng." })
        return
      }

      form.clearErrors()
      onSelect({
        skuId: sku?.id?.toString(),
        contentType: data.contentType,
        totalClip: data.totalClip
      })
    },
    [form, findSelectedSku, saleProp, products?.skus, addressInfo, onSelect]
  )

  const handleContentTypeChange = useCallback(
    (type: EAffiliateCampaignType) => {
      const current = form.getValues("contentType")
      form.setValue("contentType", toggleContentType(current, type))
    },
    [form]
  )

  const handleCheckVideo = useCallback(
    () => handleContentTypeChange(EAffiliateCampaignType.VIDEO),
    [handleContentTypeChange]
  )

  return (
    <BottomSheetModal
      isVisible={open}
      setIsVisible={onClose}
      title="Chọn sản phẩm mẫu mà bạn muốn nhận"
      description=""
    >
      <div className="p-3 pb-0 max-h-[80vh] overflow-y-auto rounded-2xl">
        {/* Address Section */}
        <Controller
          name="addressInfo"
          control={form.control}
          render={({ fieldState }) => (
            <div className="mb-4 space-y-2">
              <MessageError error={fieldState.error?.message}>
                <div className="flex justify-between items-start">
                  <p className="text-sm font-medium">Địa chỉ nhận hàng mẫu</p>
                  <button
                    onClick={onUpdateAddress}
                    className="p-1 rounded-full border hover:bg-gray-100"
                  >
                    <PencilIcon size={16} />
                  </button>
                </div>
                <div className="space-y-1 rounded-md border p-2">
                  <p className="text-xs">{addressInfo?.fullName || "Trống"}</p>
                  <p className="text-xs">{addressInfo?.address?.trim() || "Trống"}</p>
                  <p className="text-xs">{addressInfo?.phoneNumber || "Trống"}</p>
                </div>
              </MessageError>
            </div>
          )}
        />

        {/* Content Type Section */}
        <Controller
          name="contentType"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="mb-4 space-y-2">
              <MessageError error={fieldState.error?.message}>
                <p className="text-sm font-medium">Chọn hình thức lên bài</p>
                <div className="flex gap-6">
                  <label className="text-sm flex items-center gap-2">
                    <Checkbox
                      checked={field.value?.includes(EAffiliateCampaignType.VIDEO)}
                      onCheckedChange={handleCheckVideo}
                    />
                    <span className="text-sm">Video</span>
                  </label>
                </div>
              </MessageError>
            </div>
          )}
        />

        {/* Total Clips Section */}
        <Controller
          name="totalClip"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="mb-4 space-y-2">
              <MessageError error={fieldState.error?.message}>
                <p className="text-sm font-medium">Số lượng video có thể hoàn thành?</p>
                <p className="text-xs text-gray-500">Tối đa 3 video</p>
                <Input
                  type="number"
                  className="w-32 h-8 rounded-sm text-xs"
                  placeholder="Nhập số lượng"
                  onKeyDown={validateNumericInput}
                  onInput={sanitizeNumericInput}
                  {...field}
                />
              </MessageError>
            </div>
          )}
        />

        {/* SKU Selection Section */}
        <Controller
          name="sku"
          control={form.control}
          render={({ fieldState }) => (
            <div className="space-y-4">
              {fieldState.error?.message && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{fieldState.error.message}</AlertDescription>
                </Alert>
              )}
              {saleProps?.map((item: any) => (
                <div key={item?.propId} className="space-y-2">
                  <p className="text-sm font-medium">{item?.propName.trim()}</p>
                  <div className="flex flex-wrap gap-3">
                    {item?.salePropValues?.map((salePropItem: any, index: number) => (
                      <SalePropItem
                        key={index}
                        item={salePropItem}
                        onChange={(propValueId) => {
                          togglePropId(propValueId, item?.propId)
                          form.trigger("sku")
                        }}
                        propId={saleProp?.[item.propId]}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        />

        {/* Submit Button */}
        <div className="mt-6 sticky bottom-0 bg-white py-3 flex flex-col gap-1">
          <p className="text-[11px] text-gray-600">
            Lưu ý: Nhãn hàng có thể gửi sản phẩm mẫu khác với loại bạn đã chọn
          </p>
          <Button
            className="w-full h-14 bg-linear-to-r from-primary to-secondary transition-transform rounded-xl"
            disabled={isPending}
            onClick={form.handleSubmit(onSubmit)}
          >
            {isPending ? "Đang xử lý..." : "Chọn"}
          </Button>
        </div>
      </div>
    </BottomSheetModal>
  )
})

interface ApplySuccessModalProps {
  open: boolean
  onClose: () => void
  product?: AffiliateCampaign["products"]
}

const ApplySuccessModal = memo(function ApplySuccessModal({
  open,
  onClose,
  product
}: ApplySuccessModalProps) {
  const handleOpenChange = useCallback((isOpen: boolean) => !isOpen && onClose(), [onClose])

  const handleClose = useCallback(() => {
    onClose()
    useShowCampaign.getState().setId("")
  }, [onClose])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[90vw] sm:max-w-md p-0 gap-0 overflow-hidden"
        showCloseButton={false}
        isDisabledPressBack
      >
        {/* Hidden title for accessibility */}
        <DialogTitle className="sr-only">Đăng ký thành công</DialogTitle>
        <div className="relative bg-white rounded-2xl overflow-hidden">
          {/* Decorative background shapes - confetti style */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-2 left-2 w-3 h-3 bg-yellow-400/40 rotate-45 transform"></div>
            <div className="absolute top-6 right-12 w-2 h-2 bg-blue-400/40 rotate-45 transform"></div>
            <div className="absolute bottom-24 left-8 w-2.5 h-2.5 bg-orange-400/40 rotate-45 transform"></div>
            <div className="absolute top-4 left-4 w-6 h-6 bg-blue-400/30 rounded-full blur-sm"></div>
            <div className="absolute top-8 right-8 w-5 h-5 bg-yellow-400/30 rounded-full blur-sm"></div>
            <div className="absolute bottom-20 left-6 w-8 h-8 bg-green-400/30 rounded-full blur-sm"></div>
            <div className="absolute bottom-32 right-4 w-6 h-6 bg-orange-400/30 rounded-full blur-sm"></div>
            <div className="absolute top-1/2 left-1/4 w-4 h-4 bg-purple-400/30 rounded-full blur-sm"></div>
            <div className="absolute top-12 left-12 w-3 h-3 bg-green-400/40 rotate-12 transform"></div>
            <div className="absolute bottom-16 right-12 w-2.5 h-2.5 bg-pink-400/40 rotate-12 transform"></div>
            <div className="absolute top-16 right-4 w-0 h-0 border-l-[6px] border-r-[6px] border-b-10 border-l-transparent border-r-transparent border-b-blue-400/40"></div>
            <div className="absolute bottom-28 left-16 w-0 h-0 border-l-[5px] border-r-[5px] border-b-8 border-l-transparent border-r-transparent border-b-yellow-400/40"></div>
            <div className="absolute top-20 right-16 w-8 h-1 bg-linear-to-r from-transparent via-teal-400/30 to-transparent rounded-full"></div>
            <div className="absolute bottom-12 left-20 w-6 h-1 bg-linear-to-r from-transparent via-orange-400/30 to-transparent rounded-full"></div>
          </div>

          <div className="relative p-6">
            {product?.images?.[0] && (
              <div className="relative mb-4 aspect-square rounded-xl overflow-hidden bg-gray-100">
                <Image
                  src={product.images[0]}
                  alt={product.title || "Product"}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="mb-6 text-center">
              <h3 className="text-xs font-normal text-gray-900">Đăng ký thành công chiến dịch</h3>
              {product?.title && (
                <p className="text-sm font-semibold text-gray-800">&quot;{product.title}&quot;</p>
              )}
              {product?.shopName && (
                <p className="text-xs text-primary font-medium mt-2">
                  Nhãn hiệu {product.shopName}
                </p>
              )}
            </div>

            <div className="flex">
              <Button
                className={cn(
                  "flex-1 h-10! text-xs rounded-xl font-normal text-white",
                  "bg-gradient-primary"
                )}
                onClick={handleClose}
              >
                Đóng
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
})

// --------------------
// Apply button & sheet
// --------------------

interface ApplyActionButtonProps {
  status: ApplyButtonStatus
  onApply?: () => void
  onAuthorize?: () => void
  productLink?: string
  data?: AffiliateCampaign
  user?: User
  // Sample request props
  onRequestSample?: () => Promise<boolean>
  isCheckingSample?: boolean
  isSampleRegistered?: boolean
  hasKolSubmissionId?: boolean
  isFetching?: boolean
  hasPendingSample?: boolean
}

interface Actions {
  onApply?: () => void
  onAuthorizeShowCase?: () => void
  data?: AffiliateCampaign
  user?: User | null
}
interface ButtonConfig {
  label: string
  disabled: boolean
  className: string
  onClick?: () => void
  renderExtra?: (actions: Actions) => React.ReactNode
}

const BUTTON_CONFIGS: Record<ApplyButtonStatus, (actions: Actions) => ButtonConfig> = {
  [ApplyButtonStatus.CAN_APPLY]: ({ onApply, user }: Actions) => {
    return {
      label:
        user?.tiktoks?.link && user?.tiktoks?.followerCount && user?.isAuthCreatorTap
          ? "Đăng ký tham gia"
          : "Không đủ điều kiện tham gia",
      disabled: !(user?.tiktoks?.link && user?.tiktoks?.followerCount && user?.isAuthCreatorTap),
      className:
        user?.tiktoks?.link && user?.tiktoks?.followerCount && user?.isAuthCreatorTap
          ? "flex-1 w-full bg-primary-800 py-3 rounded-xl font-normal !h-12"
          : "flex-1 w-full bg-gray-300 text-black rounded-xl font-normal !h-12",
      onClick: onApply,
      renderExtra: ({ onAuthorizeShowCase, data, user }) => {
        if (
          user?.tiktoks?.link &&
          user?.tiktoks?.followerCount &&
          user?.isAuthCreatorTap &&
          (user?.tiktoks?.isAuthorized?.liveGmv || user?.tiktoks?.isAuthorized?.videoGmv)
        )
          return null

        if (data?.cashAmount) {
          return (
            <div className="flex flex-col gap-2 py-2">
              <div className="flex flex-col gap-2 py-3">
                <div className="flex gap-2 justify-between pr-2">
                  <p className="flex items-center gap-1 text-xs">
                    {user?.tiktoks?.link && user?.tiktoks?.followerCount ? (
                      <TickCircle className="h-4 w-4 text-green-500" variant="Bold" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-red-500 flex justify-center items-center">
                        <XIcon className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                    <span>Ủy quyền kênh</span>
                  </p>
                </div>
                <div className="flex gap-2 justify-between pr-2">
                  <p className="flex items-center gap-1 text-xs">
                    {user?.isAuthCreatorTap ? (
                      <TickCircle className="h-4 w-4 text-green-500" variant="Bold" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-red-500 flex justify-center items-center">
                        <XIcon className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                    <span>Ủy quyền trang trưng bày</span>
                  </p>
                  {!user?.isAuthCreatorTap && (
                    <p
                      className="text-red-500 text-xss cursor-pointer"
                      onClick={() => onAuthorizeShowCase?.()}
                    >
                      Ủy quyền ngay
                    </p>
                  )}
                </div>
                <div className="flex gap-2 justify-between pr-2">
                  <p className="flex items-center gap-1 text-xs">
                    {user?.tiktoks?.isAuthorized?.liveGmv ||
                    user?.tiktoks?.isAuthorized?.videoGmv ? (
                      <TickCircle className="h-4 w-4 text-green-500" variant="Bold" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-red-500 flex justify-center items-center">
                        <XIcon className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                    <span>Chia sẻ GMV</span>
                  </p>
                  {(!user?.tiktoks?.isAuthorized?.liveGmv ||
                    !user?.tiktoks?.isAuthorized?.videoGmv) && (
                    <p
                      className="text-red-500 text-xss cursor-pointer"
                      onClick={() => {
                        sendOpenLink(TIKTOK_URL_SCHEME)
                      }}
                    >
                      Chia sẻ GMV
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-black mb-2 font-light text-center">
                Để tham gia chiến dịch có Cast, bạn cần đáp ứng đầy đủ các yêu cầu được nêu ở trên.
                Xem chi tiết tính năng và quyền lợi của bạn
                <span
                  className="text-primary font-semibold px-1"
                  onClick={() => {
                    sendOpenLink(
                      `${import.meta.env.VITE_WEB_BRAND_URL}/blogs/cai-tien-tinh-nang-uy-quyen-trang-trung-bay-showcase-id-15`
                    )
                  }}
                >
                  TẠI ĐÂY
                </span>
              </p>
            </div>
          )
        }

        return (
          <div className="flex flex-col gap-2 py-2">
            <div className="flex gap-2 justify-between pr-2">
              <p className="flex items-center gap-1 text-xs">
                {user?.isAuthCreatorTap ? (
                  <TickCircle className="h-4 w-4 text-green-500" variant="Bold" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
                <span>Ủy quyền trang trưng bày</span>
              </p>
              {!user?.isAuthCreatorTap && (
                <p
                  className="text-red-500 text-xss cursor-pointer"
                  onClick={() => onAuthorizeShowCase?.()}
                >
                  Ủy quyền ngay
                </p>
              )}
            </div>
            <p className="text-xs text-black mb-2 font-light text-center">
              Để đảm bảo vận hành và trải nghiệm trên ứng dụng ZZP, bạn được yêu cầu ủy quyền trang
              trưng bày (Showcase) để có thể đăng ký chiến dịch này. Xem chi tiết tính năng và quyền
              lợi của bạn
              <span
                className="text-primary font-semibold px-1"
                onClick={() => {
                  sendOpenLink(
                    `${import.meta.env.VITE_WEB_BRAND_URL}/blogs/cai-tien-tinh-nang-uy-quyen-trang-trung-bay-showcase-id-15`
                  )
                }}
              >
                TẠI ĐÂY
              </span>
            </p>
          </div>
        )
      }
    }
  },

  [ApplyButtonStatus.APPLIED]: () => ({
    label: "Đã đăng ký",
    disabled: false,
    className: "flex-1 w-full bg-gray-300 text-black rounded-xl font-normal !h-14"
  }),

  [ApplyButtonStatus.RESTRICT_CAMPAIGN]: () => ({
    label: "Bạn không đủ điều kiện đăng ký",
    disabled: true,
    className: "flex-1 w-full bg-gray-300 text-black rounded-xl font-medium !h-14"
  }),

  [ApplyButtonStatus.RESTRICT_ACCOUNT]: () => ({
    label: "Tài khoản của bạn đã bị hạn chế",
    disabled: true,
    className: "flex-1 w-full bg-gray-300 text-black rounded-xl font-normal !h-12"
  }),

  [ApplyButtonStatus.NOT_AUTHORIZE_SHOW_CASE]: ({ onAuthorizeShowCase }: Actions) => ({
    label: "Không đủ điều kiện",
    disabled: true,
    className: "flex-1 w-full bg-gray-400 text-black rounded-xl font-normal !h-12",
    renderExtra: () => (
      <div className="flex flex-col gap-2 py-2">
        <div className="flex items-center justify-between">
          <p className="flex items-center justify-between gap-1 text-xs">
            <XCircle className="h-4 w-4 text-red-500" />
            <span>Ủy quyền trang trưng bày</span>
          </p>
          <p
            onClick={onAuthorizeShowCase}
            className="flex justify-between items-center gap-1 text-red-500 text-xss cursor-pointer"
          >
            Ủy quyền ngay
          </p>
        </div>
        <p className="text-xs text-black mb-2 font-light text-center">
          Để đảm bảo vận hành và trải nghiệm trên ứng dụng ZZP, bạn được yêu cầu ủy quyền trang
          trưng bày (Showcase) để có thể đăng ký chiến dịch này. Xem chi tiết tính năng và quyền lợi
          của bạn
          <span
            onClick={() => {
              sendOpenLink(
                `${import.meta.env.VITE_WEB_BRAND_URL}/blogs/cai-tien-tinh-nang-uy-quyen-trang-trung-bay-showcase-id-15`
              )
            }}
            className="text-primary font-semibold px-1"
          >
            TẠI ĐÂY
          </span>
        </p>
      </div>
    )
  }),

  [ApplyButtonStatus.FINISHED]: () => ({
    label: "Đã kết thúc",
    disabled: true,
    className: "flex-1 w-full bg-gray-300 text-black rounded-xl font-medium !h-14"
  }),

  [ApplyButtonStatus.PAUSED]: () => ({
    label: "Tạm dừng nhận ứng viên",
    disabled: true,
    className: "flex-1 w-full bg-gray-300 text-black rounded-xl font-medium !h-14"
  })
}

const ApplyActionButton = memo(
  function ApplyActionButton({
    status,
    onApply,
    onAuthorize,
    data,
    // onRequestSample,
    // isCheckingSample,
    // isSampleRegistered,
    isFetching
    // hasPendingSample
  }: ApplyActionButtonProps) {
    const { user } = useCurrentUser()
    // const [sampleRequestFailed, setSampleRequestFailed] = useState(false)

    // const showFormActions =
    //   hasPendingSample || data?.kolSubmissionStatus === "PENDING_REGISTER_SAMPLE"

    // const handleRequestClick = useCallback(async () => {
    //   if (isCheckingSample || sampleRequestFailed) return
    //   const success = await onRequestSample?.()
    //   if (!success) {
    //     setSampleRequestFailed(true)
    //     setTimeout(() => setSampleRequestFailed(false), 10000)
    //   }
    // }, [onRequestSample, isCheckingSample, sampleRequestFailed])

    // Show skeleton while fetching fresh data
    if (isFetching && !data?.kolSubmissionStatus) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-12 rounded-xl bg-gray-200 animate-pulse" />
        </div>
      )
    }

    // Show sample request buttons when status is PENDING_REGISTER_SAMPLE
    // if (showFormActions) {
    //   return (
    //     <div className="flex gap-2 w-full">
    //       {!isSampleRegistered && (
    //         <Button
    //           onClick={handleRequestClick}
    //           disabled={isCheckingSample || sampleRequestFailed}
    //           className={cn(
    //             "flex-1 h-12! rounded-xl font-semibold shadow-md transition-all",
    //             isCheckingSample || sampleRequestFailed
    //               ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
    //               : "bg-linear-to-r from-primary to-secondary text-white hover:opacity-90 active:scale-[0.98]"
    //           )}
    //         >
    //           {isCheckingSample ? (
    //             <Loader2 size={16} className="animate-spin mr-1" />
    //           ) : (
    //             <CircleCheckBig size={18} className="mr-1" />
    //           )}
    //           {isCheckingSample ? "Đang kiểm tra..." : "Đã yêu cầu"}
    //         </Button>
    //       )}

    //       {sampleRequestFailed ? (
    //         <Button
    //           onClick={() => data?.products?.link && sendOpenLink(data.products.link)}
    //           className="flex-1 h-12! rounded-xl font-semibold shadow-md transition-all bg-linear-to-r from-primary to-secondary text-white hover:opacity-90 active:scale-[0.98]"
    //         >
    //           Yêu cầu mẫu
    //         </Button>
    //       ) : (
    //         <Button
    //           disabled={true}
    //           className={cn(
    //             "flex-1 h-12! rounded-xl font-medium transition-all flex items-center justify-center gap-2 cursor-not-allowed",
    //             isSampleRegistered
    //               ? "bg-linear-to-r from-green-500 to-emerald-500 text-white"
    //               : "bg-gray-100 text-gray-400 border border-gray-200"
    //           )}
    //         >
    //           {isSampleRegistered ? (
    //             <CircleCheckBig size={16} />
    //           ) : (
    //             <Loader2 size={16} className="animate-spin" />
    //           )}
    //           {isSampleRegistered ? "Đã đăng ký" : "Đang đợi"}
    //         </Button>
    //       )}
    //     </div>
    //   )
    // }

    // Normal button rendering
    const config = BUTTON_CONFIGS[status]?.({
      onApply,
      onAuthorizeShowCase: onAuthorize,
      data,
      user
    })

    const renderExtraContent = config?.renderExtra?.({
      onApply,
      onAuthorizeShowCase: onAuthorize,
      data,
      user
    })

    if (!config) return null

    const { label, disabled, className, onClick } = config

    return (
      <>
        {renderExtraContent}
        <div className="flex items-center gap-2">
          <Button className={className} disabled={disabled} onClick={onClick}>
            {label}
          </Button>
        </div>
      </>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.status === nextProps.status &&
      prevProps.onApply === nextProps.onApply &&
      prevProps.onAuthorize === nextProps.onAuthorize &&
      prevProps.productLink === nextProps.productLink &&
      prevProps.data?.id === nextProps.data?.id &&
      prevProps.data?.kolSubmissionStatus === nextProps.data?.kolSubmissionStatus &&
      prevProps.isCheckingSample === nextProps.isCheckingSample &&
      prevProps.isSampleRegistered === nextProps.isSampleRegistered &&
      prevProps.hasKolSubmissionId === nextProps.hasKolSubmissionId &&
      prevProps.isFetching === nextProps.isFetching &&
      prevProps.hasPendingSample === nextProps.hasPendingSample
    )
  }
)

// --------------------
// Sheet UI
// --------------------

import { TickCircle } from "iconsax-reactjs"

import {
  AddressSection,
  FormActionButtons,
  FormTypeSection,
  ProductInfoSection,
  QuantitySection,
  SkuSelectionSection
} from "@/features/jobs/explore/components/campaign/actions/form-sections"
import { ModalUpdateAddress } from "@/features/users/components/informations"
import { TIKTOK_URL_SCHEME } from "@/modules/helpers/tap-constants"

function ApplyCampaignSheet() {
  const props = useApplyCampaignContext()
  const { insetTop, insetBottom } = useLayoutStore()

  return (
    <SheetContent
      side="right"
      style={{ paddingTop: insetTop, paddingBottom: insetBottom + 8 }}
      hiddenClose
    >
      <div className="flex flex-col h-full bg-white">
        <div className="flex items-center gap-1 px-4 py-3">
          <Button
            onClick={props.onClose}
            variant="ghost"
            size="sm"
            className="rounded-full w-5 h-5"
          >
            <ArrowLeftIcon />
          </Button>
          <p className="text-sm text-black">Đăng ký chiến dịch</p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
          <ProductInfoSection
            productImage={props.productImage}
            productTitle={props.productTitle}
            shopName={props.shopName}
          />

          <AddressSection
            addresses={props.addresses}
            selectedAddress={props.selectedAddress}
            onAddressClick={props.onAddressClick}
            onEditClick={props.onEditAddress}
            onAddNew={props.onAddNewAddress}
            formatAddress={formatAddress}
            errors={{
              address: props.validationErrors.address,
              fullName: props.validationErrors.fullName,
              phoneNumber: props.validationErrors.phoneNumber
            }}
          />

          <FormTypeSection
            contentTypes={props.contentTypes}
            onToggle={props.onToggleContentType}
            error={props.validationErrors.contentTypes}
          />

          <QuantitySection
            value={props.totalClip}
            onChange={props.onUpdateTotalClip}
            fixedValue={props.fixedTotalClip}
            error={props.validationErrors.totalClip}
          />

          <SkuSelectionSection
            saleProps={props.saleProps}
            productCampaignSaleProps={props.productCampaignSaleProps}
            selectedSaleProp={props.saleProp}
            onSelect={props.onTogglePropId}
            selectedSkus={props.selectedSkus}
            onSelectMultiple={props.onSelectMultiple}
            error={props.validationErrors.selectedSkuId}
            skus={props.skus}
            skuHiddens={props.skuHiddens}
          />
        </div>

        <FormActionButtons
          onCancel={props.onClose}
          onConfirm={props.onConfirm}
          isPending={props.isPending}
          onRequestSample={props.onRequestSample}
          isCheckingSample={props.isCheckingSample}
          isSampleRegistered={props.isSampleRegistered}
          hasKolSubmissionId={props.hasKolSubmissionId}
        />
      </div>
    </SheetContent>
  )
}

// --------------------
// Combined modals wrapper
// --------------------

function ApplyCampaignModals() {
  const props = useApplyCampaignContext()
  const { connectSocialProps, updateAddressProps, successModalProps } = useApplyCampaignModals({
    modals: props.modals,
    showSuccessModal: props.showSuccessModal,
    user: props.user,
    addressEditInfo: props.addressEditInfo,
    addressCount: props.addresses?.length || 0,
    addressId: props.selectedAddress?.id,
    isPending: props.isPending,
    product: props.product,
    onCloseModal: props.onCloseModal,
    onCloseSuccessModal: props.onCloseSuccessModal,
    onConnectSocialCompleted: props.onConnectSocialCompleted,
    onGMVCheckingCompleted: props.onGMVCheckingCompleted,
    onAddressModalClose: props.onAddressModalClose,
    onAddressUpdateCompleted: props.onAddressUpdateCompleted
  })

  return (
    <>
      <ConnectSocialModal {...connectSocialProps} />
      <ModalUpdateAddress {...updateAddressProps} />
      <ApplySuccessModal {...successModalProps} />
    </>
  )
}

// --------------------
// Public entry: ApplyCampaignForm
// --------------------

export function ApplyCampaignForm(props: ApplyCampaignFormProps) {
  const formData = useApplyCampaignForm(props)

  const handleAuthorize = useCallback(() => {
    if (!formData.user?.id) return
    openTikTokShowcaseAuth(formData.user.id)
  }, [formData.user?.id])

  const handleCloseSheet = useCallback(() => {
    formData.setIsSheetVisible(false)
  }, [formData])

  const handleCloseSuccessModal = useCallback(() => {
    formData.setShowSuccessModal(false)
  }, [formData])

  const contextValue = useMemo(
    () => ({
      ...formData,
      productImage: props.data?.products?.images?.[0] || "",
      productTitle: props.data?.products?.title,
      shopName: props.data?.shopName,
      skus: props.data?.products?.skus,
      skuHiddens: props.data?.skuHiddens,
      link: props.data?.products?.link,
      product: props.data?.products,
      onClose: handleCloseSheet,
      onConfirm: formData.handleConfirm,
      onAddressClick: formData.handleAddressChange,
      onEditAddress: formData.handleEditAddress,
      onAddNewAddress: formData.handleAddNewAddress,
      onToggleContentType: formData.toggleContentType,
      onTogglePropId: formData.togglePropId,
      onSelectMultiple: formData.togglePropValue,
      onUpdateTotalClip: formData.updateTotalClip,
      onCloseModal: formData.closeModal,
      onCloseSuccessModal: handleCloseSuccessModal,
      onConnectSocialCompleted: formData.handleConnectSocialCompleted,
      onGMVCheckingCompleted: formData.handleGMVCheckingCompleted,
      onAddressModalClose: formData.handleAddressModalClose,
      onAddressUpdateCompleted: formData.handleAddressUpdateCompleted
    }),
    [formData, props.data, handleCloseSheet, handleCloseSuccessModal]
  )

  return (
    <ApplyCampaignContext.Provider value={contextValue}>
      <div className="p-2">
        <ApplyActionButton
          status={formData.buttonStatus}
          onApply={formData.handleOpenSheet}
          onAuthorize={handleAuthorize}
          productLink={props?.data?.products?.link}
          data={props?.data}
          onRequestSample={formData.handleRequestSample}
          isCheckingSample={formData.isCheckingSample}
          isSampleRegistered={formData.isSampleRegistered}
          hasKolSubmissionId={formData.hasKolSubmissionId}
          isFetching={formData.isFetching}
          hasPendingSample={formData.hasPendingSample}
        />

        <Sheet open={formData.isSheetVisible} onOpenChange={formData.setIsSheetVisible} modal>
          <ApplyCampaignSheet />
        </Sheet>
        <ApplyCampaignModals />
      </div>
    </ApplyCampaignContext.Provider>
  )
}

export { ApplySuccessModal, ConnectSocialModal, GMVCheckingModal, SelectSkuModal }
